
import React, { useState } from 'react';
import {
  User,
  Mail,
  Phone,
  Shield,
  CheckCircle,
  ArrowUpDown,
  ChevronLeft,
  ChevronRight,
  Send
} from 'lucide-react';
import { UsersDetail } from '../../../types/settings/UsersTypes';
import { PaginationProps } from '../../../types/settings/settings';
import { toastMessage } from '@/utils/toast-message';
import api from '@/utils/api';

interface ModernUsersTableProps {
  initialData: UsersDetail[];
  onEdit?: (user: UsersDetail) => void;
  onUpdateUser?: (user: UsersDetail) => void;
  pagination?: PaginationProps;
  onPageChange?: (page: number) => void;
  onSort?: (column: string, order: 'ASC' | 'DESC') => Promise<void>;
  isLoading?: boolean;
  searchTerm?: string;
  sortColumn?: string;
  sortOrder?: 'ASC' | 'DESC';
}

const ModernUsersTable: React.FC<ModernUsersTableProps> = ({
  initialData,
  onEdit,
  onUpdateUser,
  pagination,
  onPageChange,
  onSort,
  isLoading = false,
  sortColumn,
  sortOrder,
}) => {
  const [resendingEmail, setResendingEmail] = useState<string | null>(null);
  const [togglingActive, setTogglingActive] = useState<string | null>(null);

  const handleSort = (column: string) => {
    if (onSort) {
      const newOrder = column === sortColumn && sortOrder === 'ASC' ? 'DESC' : 'ASC';
      onSort(column, newOrder);
    }
  };

  const handleResendEmail = async (userUuid: string, userEmail: string) => {
    try {
      setResendingEmail(userUuid);
      const response = await api.req({
        url: `/users/${userUuid}/resend-verification`,
        method: api.Method.POST,
      });

      if (response.status === 'success') {
        toastMessage(`Verification email sent to ${userEmail}`, 'success');
      } else {
        throw new Error(response.message || 'Failed to send email');
      }
    } catch (error: any) {
      toastMessage(error.message || 'Failed to send verification email', 'error');
    } finally {
      setResendingEmail(null);
    }
  };

  const handleToggleActive = async (user: UsersDetail) => {
    const newValue = !user.user_active;
    if (!newValue && !window.confirm(
      `Make ${user.userName} inactive?\n\n` +
      `• They stop receiving new leads and cannot log in.\n` +
      `• Their open order items (EDD / NDR / RTO) go back to the pool.\n` +
      `• Their customers become Unassigned. Each one gets a new owner through Round Robin when a new cart, order or reorder arrives for them.`
    )) {
      return;
    }
    try {
      setTogglingActive(user.uuid);
      const response = await api.req({
        url: `/users/${user.uuid}`,
        method: api.Method.POST,
        body: { user_active: newValue }
      });
      if (response.status === 'success') {
        const released = (response.data as { items_released?: number } | null)?.items_released ?? 0;
        onUpdateUser?.({ ...user, user_active: newValue });
        toastMessage(
          newValue
            ? 'User activated'
            : `User made inactive${released ? `; ${released} open order item(s) released` : ''}`,
          'success'
        );
      } else {
        throw new Error(response.message || 'Failed to update status');
      }
    } catch (error: any) {
      toastMessage(error.message || 'Failed to update status', 'error');
    } finally {
      setTogglingActive(null);
    }
  };

  const RoleBadge = ({ role }: { role?: { role_name?: string; uuid?: string } }) => (
    <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-indigo-100 text-indigo-800 dark:bg-indigo-900/20 dark:text-indigo-400">
      <Shield className="w-3 h-3 mr-1" />
      {role?.role_name || 'No Role'}
    </span>
  );

  const SortButton = ({ column, label }: { column: string; label: string }) => (
    <button
      onClick={() => handleSort(column)}
      className="flex items-center space-x-1 hover:text-brandColor dark:hover:text-blue-400 transition-colors"
    >
      <span>{label}</span>
      <ArrowUpDown className="w-3 h-3" />
    </button>
  );

  const UserTableSkeleton = () => (
    <div className="bg-white dark:bg-gray-800 shadow-sm rounded-lg border border-gray-200 dark:border-gray-700">
      <div className="p-6 border-b border-gray-200 dark:border-gray-700">
        <div className="flex gap-4">
          <div className="flex-1 h-10 bg-gray-200 dark:bg-gray-700 rounded animate-pulse" />
          <div className="w-24 h-10 bg-gray-200 dark:bg-gray-700 rounded animate-pulse" />
        </div>
      </div>
      <div className="p-6">
        {[...Array(5)].map((_, i) => (
          <div key={i} className="flex items-center space-x-4 mb-4">
            <div className="w-10 h-10 bg-gray-200 dark:bg-gray-700 rounded-full animate-pulse" />
            <div className="flex-1 space-y-2">
              <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-1/4 animate-pulse" />
              <div className="h-3 bg-gray-200 dark:bg-gray-700 rounded w-1/3 animate-pulse" />
            </div>
          </div>
        ))}
      </div>
    </div>
  );

  if (isLoading) {
    return <UserTableSkeleton />;
  }

  return (
    <div className="h-full flex flex-col bg-white dark:bg-gray-800 shadow-sm rounded-lg border border-gray-200 dark:border-gray-700">
      {/* Table — flex-1 + min-h-0 makes only this area scroll */}
      <div className="flex-1 overflow-auto min-h-0">
        <table className="w-full divide-y divide-gray-200 dark:divide-gray-700">
          <thead className="bg-gray-50 dark:bg-gray-700 sticky top-0 z-10">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                <SortButton column="userName" label="User" />
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                Contact Info
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                Shopify Info
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                Status
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                Actions
              </th>
            </tr>
          </thead>
          <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
            {initialData.map((user) => {
              const isDeleted = !!user.deleted_at;
              return (
              <tr key={user.uuid} className={`hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors ${!user.user_active ? 'opacity-70' : ''}`}>
                {/* User Info */}
                <td className="px-6 py-4 whitespace-nowrap">
                  <div className="flex items-center">
                    <div className="flex-shrink-0 h-10 w-10">
                      <div className="h-10 w-10 rounded-full bg-blue-100 dark:bg-blue-900/20 flex items-center justify-center">
                        <User className="h-5 w-5 text-brandColor dark:text-blue-400" />
                      </div>
                    </div>
                    <div className="ml-4">
                      {isDeleted ? (
                        <div className="text-sm font-medium text-gray-500 dark:text-gray-400 line-through">
                          {user.userName}
                        </div>
                      ) : (
                        <button
                          onClick={() => onEdit?.(user)}
                          className="text-left hover:bg-blue-50 dark:hover:bg-blue-900/20 rounded-lg p-2 -m-2 transition-colors"
                        >
                          <div className="text-sm font-medium text-brandColor dark:text-blue-400 hover:text-brandColor dark:hover:text-blue-300">
                            {user.userName}
                          </div>
                        </button>
                      )}
                      <div className="mt-1">
                        <RoleBadge role={user.role} />
                      </div>
                    </div>
                  </div>
                </td>

                {/* Contact Info */}
                <td className="px-6 py-4">
                  <div className="space-y-1">
                    <div className="flex items-center text-sm text-gray-900 dark:text-white">
                      <Mail className="w-3 h-3 mr-2 text-gray-400" />
                      {user.userEmail}
                    </div>
                    {user.phone && (
                      <div className="flex items-center text-sm text-gray-500 dark:text-gray-400">
                        <Phone className="w-3 h-3 mr-2 text-gray-400" />
                        {user.phone}
                      </div>
                    )}
                  </div>
                </td>

                {/* Shopify Info */}
                <td className="px-6 py-4">
                  <div className="space-y-1">
                    {user.agent_crm_tag ? (
                      <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-violet-100 text-violet-800 dark:bg-violet-900/20 dark:text-violet-400">
                        {user.agent_crm_tag}
                      </span>
                    ) : null}
                    {user.agent_discount_code ? (
                      <div className="text-xs font-mono text-gray-500 dark:text-gray-400">
                        {user.agent_discount_code}
                      </div>
                    ) : null}
                    {!user.agent_crm_tag && !user.agent_discount_code && (
                      <span className="text-xs text-gray-400 dark:text-gray-500">—</span>
                    )}
                  </div>
                </td>

                {/* Status */}
                <td className="px-6 py-4">
                  {isDeleted ? (
                    <div className="space-y-0.5">
                      <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-red-100 text-red-800 dark:bg-red-900/20 dark:text-red-400">
                        Deleted
                      </span>
                      <div className="text-xs text-gray-500 dark:text-gray-400">
                        {user.deleted_at ? new Date(user.deleted_at).toLocaleString() : ''}
                        {user.deleted_by_name ? ` · by ${user.deleted_by_name}` : ''}
                      </div>
                    </div>
                  ) : (
                  <div className="flex items-center gap-4">
                    <div className="flex items-center gap-2">
                      <button
                        onClick={() => handleToggleActive(user)}
                        disabled={togglingActive === user.uuid}
                        className={`relative inline-flex h-5 w-9 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none disabled:opacity-50 disabled:cursor-not-allowed ${
                          user.user_active ? 'bg-green-500' : 'bg-gray-300 dark:bg-gray-600'
                        }`}
                        title={user.user_active ? 'Click to deactivate' : 'Click to activate'}
                      >
                        <span className={`pointer-events-none inline-block h-4 w-4 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${
                          user.user_active ? 'translate-x-4' : 'translate-x-0'
                        }`} />
                      </button>
                      <span className={`text-xs font-medium ${user.user_active ? 'text-green-700 dark:text-green-400' : 'text-gray-500 dark:text-gray-400'}`}>
                        {togglingActive === user.uuid ? '...' : user.user_active ? 'Active' : 'Inactive'}
                      </span>
                    </div>
                  </div>
                  )}
                  {!user.user_active && !isDeleted && typeof user.assigned_leads_count === 'number' && user.assigned_leads_count > 0 && (
                    <div className="mt-1 text-xs text-amber-700 dark:text-amber-400">
                      {user.assigned_leads_count.toLocaleString()} lead(s) still assigned
                    </div>
                  )}
                </td>

                {/* Actions */}
                <td className="px-6 py-4">
                  {isDeleted ? (
                    <span className="text-xs text-gray-400 dark:text-gray-500">Read-only</span>
                  ) : !user.user_verified ? (
                    <button
                      onClick={() => handleResendEmail(user.uuid, user.userEmail)}
                      disabled={resendingEmail === user.uuid}
                      className="inline-flex items-center px-3 py-1.5 text-xs font-medium text-white bg-brandColor rounded-lg hover:bg-brandColor focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-brandColor disabled:opacity-50 disabled:cursor-not-allowed transition-all"
                      title="Resend verification email"
                    >
                      <Send className="w-3 h-3 mr-1" />
                      {resendingEmail === user.uuid ? 'Sending...' : 'Resend Email'}
                    </button>
                  ) : (
                    <span className="inline-flex items-center px-3 py-1.5 text-xs font-medium text-green-700 bg-green-50 rounded-lg">
                      <CheckCircle className="w-3 h-3 mr-1" />
                      Verified
                    </span>
                  )}
                </td>
              </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Empty State */}
      {initialData.length === 0 && (
        <div className="text-center py-12">
          <User className="mx-auto h-12 w-12 text-gray-400" />
          <h3 className="mt-2 text-sm font-medium text-gray-900 dark:text-white">No users found</h3>
          <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
            {'Get started by creating a new user'}
          </p>
        </div>
      )}

      {/* Pagination */}
      {pagination && pagination.totalPages > 1 && (
        <div className="bg-white dark:bg-gray-800 px-4 py-3 border-t border-gray-200 dark:border-gray-700 sm:px-6">
          <div className="flex items-center justify-between">
            <div className="flex-1 flex justify-between sm:hidden">
              <button
                onClick={() => onPageChange?.(pagination.currentPage - 1)}
                disabled={pagination.currentPage <= 1}
                className="relative inline-flex items-center px-4 py-2 border border-gray-300 dark:border-gray-600 text-sm font-medium rounded-md text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 hover:bg-gray-50 dark:hover:bg-gray-600 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Previous
              </button>
              <button
                onClick={() => onPageChange?.(pagination.currentPage + 1)}
                disabled={pagination.currentPage >= pagination.totalPages}
                className="ml-3 relative inline-flex items-center px-4 py-2 border border-gray-300 dark:border-gray-600 text-sm font-medium rounded-md text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 hover:bg-gray-50 dark:hover:bg-gray-600 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Next
              </button>
            </div>
            <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
              <div>
                <p className="text-sm text-gray-700 dark:text-gray-300">
                  Showing{' '}
                  <span className="font-medium">
                    {(pagination.currentPage - 1) * (pagination.itemsPerPage || 10) + 1}
                  </span>{' '}
                  to{' '}
                  <span className="font-medium">
                    {Math.min(pagination.currentPage * (pagination.itemsPerPage || 10), pagination.totalItems)}
                  </span>{' '}
                  of{' '}
                  <span className="font-medium">{pagination.totalItems}</span>{' '}
                  results
                </p>
              </div>
              <div>
                <nav className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px" aria-label="Pagination">
                  <button
                    onClick={() => onPageChange?.(pagination.currentPage - 1)}
                    disabled={pagination.currentPage <= 1}
                    className="relative inline-flex items-center px-2 py-2 rounded-l-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-sm font-medium text-gray-500 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-600 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <ChevronLeft className="h-5 w-5" />
                  </button>
                  {Array.from({ length: Math.min(5, pagination.totalPages) }, (_, i) => {
                    const pageNumber = i + 1;
                    const isCurrentPage = pageNumber === pagination.currentPage;
                    return (
                      <button
                        key={pageNumber}
                        onClick={() => onPageChange?.(pageNumber)}
                        className={`relative inline-flex items-center px-4 py-2 border text-sm font-medium ${
                          isCurrentPage
                            ? 'z-10 bg-blue-50 dark:bg-blue-900/20 border-blue-500 dark:border-blue-400 text-brandColor dark:text-blue-400'
                            : 'bg-white dark:bg-gray-700 border-gray-300 dark:border-gray-600 text-gray-500 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-600'
                        }`}
                      >
                        {pageNumber}
                      </button>
                    );
                  })}
                  <button
                    onClick={() => onPageChange?.(pagination.currentPage + 1)}
                    disabled={pagination.currentPage >= pagination.totalPages}
                    className="relative inline-flex items-center px-2 py-2 rounded-r-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-sm font-medium text-gray-500 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-600 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <ChevronRight className="h-5 w-5" />
                  </button>
                </nav>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ModernUsersTable;